export const PORTAL_DEFAULT_ROUTE = "/" as const;

export const PORTAL_NAVIGATION_ITEMS = [
  {
    to: "/",
    label: "Executive Dashboard",
    description: "Verified warranty metrics and source evidence",
    icon: "dashboard",
  },
  {
    to: "/portfolio",
    label: "All Programs",
    description: "Division and vehicle-line coverage; program catalog availability",
    icon: "portfolio",
  },
  {
    to: "/signals",
    label: "Signal Intelligence",
    description: "Source-ranked warranty patterns for review",
    icon: "signals",
  },
  {
    to: "/problem-records",
    label: "Problem Records",
    description: "Searchable evidence and action worklist",
    icon: "records",
  },
  {
    to: "/claims",
    label: "Warranty Claims",
    description: "Imported claim volume and failure descriptions",
    icon: "claims",
  },
  {
    to: "/recall-safety",
    label: "Safety & NHTSA",
    description: "Evidence-led safety review; source authority remains explicit",
    icon: "safety",
  },
  {
    to: "/compliance",
    label: "IATF & ISO 9001",
    description: "Compliance evidence requirements and verdict availability",
    icon: "compliance",
  },
  {
    to: "/closed-loop",
    label: "Closed Loop",
    description: "Problem Record review and workflow availability",
    icon: "loop",
  },
  {
    to: "/top5",
    label: "Top 5 Intelligence",
    description: "Ranked warranty intelligence",
    icon: "top5",
  },
  {
    to: "/effectiveness-prevention",
    label: "Prevention",
    description: "Effectiveness and read-across",
    icon: "prevention",
  },
  {
    to: "/suppliers",
    label: "Suppliers",
    description: "Source-backed supplier rankings",
    icon: "suppliers",
  },
  {
    to: "/financial-intelligence",
    label: "Financial",
    description: "Governed financial context",
    icon: "financial",
  },
  {
    to: "/executive",
    label: "Executive Summary",
    description: "Leadership view",
    icon: "executive",
  },
  {
    to: "/brief",
    label: "Briefing",
    description: "Decision-ready summary",
    icon: "brief",
  },
  {
    to: "/data-confidence",
    label: "Data Confidence",
    description: "Source quality and freshness",
    icon: "confidence",
  },
] as const;

export type PortalNavigationItem = (typeof PORTAL_NAVIGATION_ITEMS)[number];
export type PortalNavigationTarget = PortalNavigationItem["to"];

export const WORKSPACE_NAVIGATION_GROUPS = [
  {
    id: "dashboard",
    label: "Dashboard",
    description: "What needs attention now",
    icon: "dashboard",
    primaryTo: "/",
    routes: ["/", "/portfolio", "/data-confidence"],
  },
  {
    id: "issues",
    label: "Issues",
    description: "Signals, priorities, claims, and risk",
    icon: "signals",
    primaryTo: "/signals",
    routes: ["/signals", "/top5", "/claims", "/recall-safety", "/compliance"],
  },
  {
    id: "investigations",
    label: "Investigations",
    description: "Problem Records and corrective action",
    icon: "records",
    primaryTo: "/problem-records",
    routes: ["/problem-records", "/closed-loop"],
  },
  {
    id: "performance",
    label: "Performance",
    description: "Effectiveness, prevention, and exposure",
    icon: "prevention",
    primaryTo: "/effectiveness-prevention",
    routes: ["/effectiveness-prevention", "/suppliers", "/financial-intelligence"],
  },
  {
    id: "reports",
    label: "Reports",
    description: "Leadership summaries and briefings",
    icon: "executive",
    primaryTo: "/executive",
    routes: ["/executive", "/brief"],
  },
] as const satisfies readonly {
  id: string;
  label: string;
  description: string;
  icon: PortalNavigationItem["icon"];
  primaryTo: PortalNavigationTarget;
  routes: readonly PortalNavigationTarget[];
}[];

export function portalNavigationItem(to: PortalNavigationTarget): PortalNavigationItem {
  const item = PORTAL_NAVIGATION_ITEMS.find((candidate) => candidate.to === to);
  if (!item) throw new Error(`Portal navigation item is missing for ${to}`);
  return item;
}

export function portalRouteIsActive(pathname: string, to: PortalNavigationTarget): boolean {
  if (to === "/") return pathname === "/" || pathname === "/dashboard";
  if (to === "/problem-records") {
    return pathname === to || pathname.startsWith("/problem-record/");
  }
  return pathname === to;
}

export function workspaceNavigationGroup(pathname: string) {
  return (
    WORKSPACE_NAVIGATION_GROUPS.find((group) =>
      group.routes.some((route) => portalRouteIsActive(pathname, route)),
    ) ?? WORKSPACE_NAVIGATION_GROUPS[0]
  );
}
